import path from 'path';
import {Plugins} from '@packages/systems/wfdl';
import get from 'lodash/get';
import {pageUsesIx2} from '@packages/systems/ix2/shared-utils';
import {emptyArray} from '@packages/utilities/fp/utils';
import type {RawStyleBlockServerType} from '@packages/systems/core/utils/StyleTypes';
import {
  generateStyleBlockMap,
  getIndirectlyAppliedClassStyleBlockIds,
} from '@packages/utilities/platform';

import {weakMemo} from '@packages/utilities/memo';

// Generates a map of style blocks ids and their values, and just the values
//   Memoized to avoid generating for every symbol and node
const getStyleMapAndValues = weakMemo((styles: [RawStyleBlockServerType]) => {
  const styleMap = generateStyleBlockMap(styles);
  return {styleMap, styleMapValues: Object.values(styleMap)};
});

// Ids of all symbol components
// TODO(VXP-87): Get per-page symbols
//   Memoized to avoid generating for every symbol and node
const getSymbolsMap = weakMemo((symbols: [{_id: string}]) => {
  return new Set<string>(symbols.map((symbol: {_id: string}) => symbol._id));
});

// Ids of style blocks targeted by CLASS IX2 events
//   Memoized to avoid generating for every symbol and node
const getIx2EventStyleIds = weakMemo(
  (
    events: Array<{target?: {appliesTo?: string; styleBlockIds?: string[]}}>
  ) => {
    const ids = new Set<string>();
    for (const event of events) {
      if (event?.target?.appliesTo === 'CLASS') {
        event.target.styleBlockIds?.forEach((id) => ids.add(id));
      }
    }
    return ids;
  }
);

const getClassSelectorParts = (block?: RawStyleBlockServerType | null) =>
  block?.data.type === 'class' && block.data.sel
    ? block.data.sel.split('.').filter(Boolean)
    : null;

// Same match rule as getIndirectlyAppliedClassStyleBlockIds, but only checks
// the candidate ids, so cost doesn't grow with the site's combo class count.
const getIndirectlyAppliedCandidateStyleIds = (
  directStyleIds: string[],
  candidateStyleIds: Set<string>,
  styleMap: Record<string, RawStyleBlockServerType | null | undefined>
): string[] => {
  const directSet = new Set(directStyleIds);
  const coveredParts = new Set<string>();
  for (const id of directStyleIds) {
    getClassSelectorParts(styleMap[id])?.forEach((part) =>
      coveredParts.add(part)
    );
  }

  const matches: string[] = [];
  for (const id of candidateStyleIds) {
    if (directSet.has(id)) continue;
    const parts = getClassSelectorParts(styleMap[id]);
    if (parts?.length && parts.every((part) => coveredParts.has(part))) {
      matches.push(id);
    }
  }
  return matches;
};

export const plugin = Plugins.PrivilegedPlugin({
  siteModules: [
    // Global (Loads on every site)
    {
      filePaths: [
        path.resolve(__dirname, 'webflow-brand.js'),
        path.resolve(__dirname, 'webflow-links.js'),
        path.resolve(__dirname, 'webflow-scroll.js'),
        path.resolve(__dirname, 'webflow-focus.js'),
        path.resolve(__dirname, 'webflow-focus-visible.js'),
        path.resolve(__dirname, 'webflow-touch.js'),
      ],
      bundleIf: {
        type: 'site',
        test: () => true,
      },
    },
    // Editor
    {
      filePaths: [path.resolve(__dirname, 'webflow-edit.js')],
      bundleIf: {
        type: 'site',
        test: ({exportMode}) => !exportMode,
      },
    },
    // IX (Legacy)
    {
      filePaths: [
        path.resolve(__dirname, 'webflow-ix.js'),
        path.resolve(__dirname, 'webflow-ix-events.js'),
      ],
      bundleIf: {
        type: 'site',
        test: (json) => get(json, 'styles.data.macros.length'),
      },
    },
    // IX2
    {
      filePaths: [
        path.resolve(__dirname, 'webflow-ix2.js'),
        path.resolve(__dirname, 'webflow-ix2-events.js'),
      ],
      bundleIf: {
        type: 'node',
        test: (node, page, json) => {
          // check if this page needs the interactions stuff
          const ix2Events = json?.interactions?.ix2?.events;
          if (!ix2Events?.length) return false;

          const ix2Interactions = json?.interactions?.ix2?.actionLists;
          const {styleMap, styleMapValues} = getStyleMapAndValues(
            json?.styles?.styles ?? emptyArray
          );
          const styleIds = node.classes ?? [];
          // pageUsesIx2 only intersects nodeStyles with CLASS event style ids,
          // so the flagged path only resolves indirect styles for those ids.
          const nodeStyles = new Set<string>([
            ...styleIds,
            ...(json?.ix2EventStylesOnly
              ? getIndirectlyAppliedCandidateStyleIds(
                  styleIds,
                  getIx2EventStyleIds(ix2Events),
                  styleMap
                )
              : getIndirectlyAppliedClassStyleBlockIds(
                  styleIds,
                  styleMap,
                  styleMapValues
                )),
          ]);
          const usesIx2 = pageUsesIx2(
            page?._id.toString() ?? node._id.toString(),
            ix2Events,
            ix2Interactions,
            getSymbolsMap(json?.symbols ?? emptyArray),
            nodeStyles
          );

          // IX2 found on this node
          return usesIx2;
        },
      },
    },
    {
      filePaths: [path.resolve(__dirname, 'webflow-analytics.js')],
      bundleIf: {
        type: 'site',
        test: (json) => get(json, 'site.intellimizeCustomerId'),
      },
    },
  ],
});
